// SpaBookings.js  Transaction page: list bookings, create new booking,
// add payment, generate invoice, cancel.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Card, CardContent, Paper, Stack, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, useMediaQuery, useTheme,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Snackbar, Alert, CircularProgress, MenuItem,
  Divider, Tooltip, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import HotelIcon from '@mui/icons-material/Hotel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { spaApi, lookupApi, ctx } from './modulesApi';

const STATUSES = ['', 'Booked', 'InProgress', 'Completed', 'Cancelled'];

const isoLocal = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function SpaBookings() {
  const { companyId, hotelId, userId, currency } = ctx();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // New booking dialog state
  const [open, setOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [guests, setGuests] = useState([]);
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [form, setForm] = useState({
    guestName: '', guestMobile: '', roomNumber: '',
    isWalkIn: true, bookingId: null,
    slotStart: isoLocal(new Date()),
    slotEnd: isoLocal(new Date(Date.now() + 60 * 60000)),
    remarks: '', discountAmount: 0, chargeToRoom: false,
    // Start with one empty service line so the user can add a service right
    // away without first clicking "Add line".
    items: [{ serviceId: 0, therapistId: null, durationMinutes: 60, unitPrice: 0, gstAmount: 0, lineTotal: 0 }],
  });

  // Payment dialog state
  const [payOpen, setPayOpen] = useState(false);
  const [payment, setPayment] = useState({ spaBookingId: 0, paymentMode: 'Cash', amount: 0, referenceNo: '' });

  // View-details dialog state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const openDetails = async (id) => {
    setDetailsOpen(true);
    setDetailsBooking(null);
    setDetailsLoading(true);
    try {
      const r = await spaApi.getBooking(id);
      setDetailsBooking(r.data);
    } catch { notify('Failed to load details', 'error'); }
    finally { setDetailsLoading(false); }
  };

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await spaApi.listBookings(hotelId, {
        status: statusFilter || undefined,
        page: page + 1, pageSize: rowsPerPage,
      });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } catch { notify('Failed to load', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage, statusFilter]);

  const openCreate = async () => {
    try {
      const [svc, thp, gs] = await Promise.all([
        spaApi.listServices(hotelId, { pageSize: 200 }),
        spaApi.listTherapists(hotelId, { pageSize: 200 }),
        lookupApi.inHouseGuests(hotelId).catch((e) => {
          console.error('[InHouseGuests] failed', e);
          return { data: [] };
        }),
      ]);
      setServices(svc.data.items || []);
      setTherapists(thp.data.items || []);
      setGuests(gs.data || []);
      setSelectedGuestId('');
      setOpen(true);
    } catch { notify('Failed to load services/therapists', 'error'); }
  };

  const onPickGuest = (bookingId) => {
    setSelectedGuestId(bookingId);
    if (!bookingId) {
      setForm({ ...form, bookingId: null, isWalkIn: true, guestName: '', guestMobile: '', roomNumber: '', chargeToRoom: false });
      return;
    }
    const g = guests.find((x) => x.bookingId === bookingId);
    if (!g) return;
    setForm({
      ...form,
      bookingId: g.bookingId,
      isWalkIn: false,
      guestName: g.guestName,
      guestMobile: g.mobileNumber || '',
      roomNumber: g.roomNumber || '',
      chargeToRoom: true,
    });
  };

  const addLine = () => setForm({
    ...form, items: [...form.items, { serviceId: 0, therapistId: null, durationMinutes: 60, unitPrice: 0, gstAmount: 0, lineTotal: 0 }],
  });

  const updateLine = (idx, patch) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };

  const onPickService = (idx, serviceId) => {
    const svc = services.find((s) => s.serviceId === serviceId);
    if (!svc) return;
    const gst = +(svc.price * (svc.gstPercent / 100)).toFixed(2);
    updateLine(idx, {
      serviceId: svc.serviceId, durationMinutes: svc.durationMinutes,
      unitPrice: svc.price, gstAmount: gst, lineTotal: svc.price + gst,
    });
  };

  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  // GST policy: every line uses its own service master GST% — same rate for
  // walk-in and charge-to-room. The backend mirrors this so modal = spa list
  // = folio = invoice all show the same number.
  const totals = useMemo(() => {
    const sub = form.items.reduce((a, b) => a + (b.unitPrice || 0), 0);
    const disc = parseFloat(form.discountAmount) || 0;
    const gst = form.items.reduce((a, b) => a + (b.gstAmount || 0), 0);
    return { sub, gst, tot: sub + gst - disc };
  }, [form.items, form.discountAmount]);

  const onCreate = async () => {
    if (!form.guestName) return notify('Guest name required', 'warning');
    if (form.items.length === 0) return notify('Add at least one service', 'warning');
    if (form.items.some((i) => !i.serviceId)) return notify('Pick service for every line', 'warning');
    try {
      const dto = {
        companyId, hotelId,
        bookingId: form.bookingId,
        guestName: form.guestName, guestMobile: form.guestMobile,
        roomNumber: form.roomNumber, isWalkIn: form.isWalkIn,
        slotStart: form.slotStart, slotEnd: form.slotEnd,
        remarks: form.remarks,
        discountAmount: parseFloat(form.discountAmount) || 0,
        chargeToRoom: form.chargeToRoom, createdBy: userId,
        items: form.items,
      };
      await spaApi.createBooking(dto);
      notify('Booking created'); setOpen(false); load();
    } catch { notify('Create failed', 'error'); }
  };

  const onCancel = async (id) => {
    const reason = window.prompt('Cancellation reason?'); if (!reason) return;
    try { await spaApi.cancelBooking(id, reason, userId); notify('Cancelled'); load(); }
    catch { notify('Cancel failed', 'error'); }
  };

  const onInvoice = async (id) => {
    try {
      const r = await spaApi.generateInvoice(id, userId);
      notify(`Invoice ${r.data.invoiceNo} generated`);
      // Open the full booking + print the invoice so it's immediately viewable.
      const det = await spaApi.getBooking(id);
      printSpaInvoice({ ...det.data, invoiceNo: r.data.invoiceNo });
    } catch { notify('Invoice failed', 'error'); }
  };

  // Open a printable spa invoice in a new window from the booking detail.
  const printSpaInvoice = (b) => {
    if (!b) return;
    const comp = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
    const cur = comp?.currency || '₹';
    const invNo = b.invoiceNo || b.invoiceNumber || `SPA-${b.spaBookingId}`;
    const due = (Number(b.totalAmount) || 0) - (Number(b.paidAmount) || 0);
    const rows = (b.items || [])
      .map(
        (it) =>
          `<tr><td>${it.serviceName || '#' + it.serviceId}</td><td>${
            it.therapistName || '-'
          }</td><td style="text-align:right">${it.durationMinutes} min</td><td style="text-align:right">${cur}${Number(
            it.unitPrice,
          ).toFixed(2)}</td><td style="text-align:right">${cur}${Number(
            it.gstAmount,
          ).toFixed(2)}</td><td style="text-align:right">${cur}${Number(
            it.lineTotal,
          ).toFixed(2)}</td></tr>`,
      )
      .join('');
    const html = `<html><head><title>${invNo}</title>
      <style>body{font-family:Arial,sans-serif;font-size:13px;color:#222;padding:16px;}
      h2{margin:0;} table{width:100%;border-collapse:collapse;margin-top:10px;}
      th,td{border:1px solid #ccc;padding:5px;font-size:12px;} th{background:#f0f0f0;text-align:left;}
      .r{text-align:right;} .tot{font-weight:700;font-size:15px;}</style></head><body>
      <div style="text-align:center"><h2>${comp?.name || 'Spa'}</h2>
      <div>SPA INVOICE</div><div><b>${invNo}</b></div></div><hr/>
      <div><b>Guest:</b> ${b.guestName || '-'} &nbsp; <b>Mobile:</b> ${
        b.guestMobile || '-'
      }</div>
      <div><b>Room:</b> ${
        b.roomNumber || (b.isWalkIn ? 'Walk-in' : '-')
      } &nbsp; <b>Date:</b> ${
        b.slotStart ? new Date(b.slotStart).toLocaleString('en-GB') : '-'
      }</div>
      <table><thead><tr><th>Service</th><th>Therapist</th><th class="r">Dur</th><th class="r">Price</th><th class="r">GST</th><th class="r">Total</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <div style="text-align:right;margin-top:10px">
        Subtotal: ${cur}${Number(b.subTotal || 0).toFixed(2)}<br/>
        GST: ${cur}${Number(b.gstAmount || 0).toFixed(2)}<br/>
        ${
          Number(b.discountAmount) > 0
            ? `Discount: -${cur}${Number(b.discountAmount).toFixed(2)}<br/>`
            : ''
        }
        <span class="tot">Total: ${cur}${Number(b.totalAmount || 0).toFixed(2)}</span><br/>
        Paid: ${cur}${Number(b.paidAmount || 0).toFixed(2)}<br/>
        Due: ${cur}${due.toFixed(2)}
      </div><hr/><div style="text-align:center;font-size:11px">Thank you!</div></body></html>`;
    const w = window.open('', '_blank', 'width=440,height=680');
    if (!w) {
      notify('Allow pop-ups to view/print the invoice', 'warning');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const openPayment = (booking) => {
    const due = (booking.totalAmount || 0) - (booking.paidAmount || 0);
    setPayment({ spaBookingId: booking.spaBookingId, paymentMode: 'Cash', amount: due, referenceNo: '' });
    setPayOpen(true);
  };

  const onAddPayment = async () => {
    if (!payment.amount || payment.amount <= 0) return notify('Enter amount', 'warning');
    try {
      await spaApi.addPayment({ ...payment, createdBy: userId });
      notify('Payment recorded'); setPayOpen(false); load();
    } catch { notify('Payment failed', 'error'); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, px: { xs: 1.5, sm: 3 } }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1} mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <EventIcon color='primary' />
              <Typography variant='h5'>Spa Bookings</Typography>
            </Box>
            <Button variant='contained' startIcon={<AddIcon />} onClick={openCreate}>
              New Booking
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth size='small' label='Status'
                value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUSES.map((s) => <MenuItem key={s || 'all'} value={s}>{s || 'All'}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          {isPhone ? (
            <Stack spacing={1.2}>
              {loading && (<Box textAlign='center' py={2}><CircularProgress size={24} /></Box>)}
              {!loading && items.length === 0 && (
                <Typography align='center' color='text.secondary' py={2}>No bookings</Typography>
              )}
              {!loading && items.map((b) => (
                <Paper key={b.spaBookingId} variant='outlined' sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>#{b.spaBookingId} · {b.guestName}</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{b.guestMobile}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {b.bookingId ? `Booking #${b.bookingId}` : (b.isWalkIn ? 'Walk-in' : '-')}{b.roomNumber ? ` · Room ${b.roomNumber}` : ''}
                      </Typography>
                    </Box>
                    <Chip size='small' label={b.status} sx={{ flexShrink: 0 }}
                      color={b.status === 'Completed' ? 'success'
                            : b.status === 'Cancelled' ? 'error'
                            : b.status === 'InProgress' ? 'warning' : 'info'} />
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                    {new Date(b.slotStart).toLocaleString('en-GB')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: 0.75 }}>
                    <Typography sx={{ fontSize: '0.74rem' }}>Total: <b>{currency} {Number(b.totalAmount).toFixed(2)}</b></Typography>
                    <Typography sx={{ fontSize: '0.74rem' }}>Paid: <b>{currency} {Number(b.paidAmount).toFixed(2)}</b></Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: ((b.totalAmount || 0) - (b.paidAmount || 0)) > 0 ? '#c62828' : '#2e7d32' }}>
                      Due: <b>{currency} {Number((b.totalAmount || 0) - (b.paidAmount || 0)).toFixed(2)}</b>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Tooltip title='View Items'>
                      <IconButton size='small' onClick={() => openDetails(b.spaBookingId)}><VisibilityIcon fontSize='small' /></IconButton>
                    </Tooltip>
                    <Tooltip title='Add Payment'>
                      <span>
                        <IconButton size='small' disabled={b.status === 'Cancelled'} onClick={() => openPayment(b)}><PaymentIcon fontSize='small' /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Invoice'>
                      <IconButton size='small' onClick={() => onInvoice(b.spaBookingId)}><ReceiptIcon fontSize='small' /></IconButton>
                    </Tooltip>
                    <Tooltip title='Cancel'>
                      <span>
                        <IconButton size='small' sx={{ color: '#2563eb' }} disabled={b.status === 'Cancelled'} onClick={() => onCancel(b.spaBookingId)}><CancelIcon fontSize='small' /></IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table size='small' sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Booking #</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Total</TableCell>
                  <TableCell align='right'>Paid</TableCell>
                  <TableCell align='right'>Due</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={10} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
                {!loading && items.length === 0 && (<TableRow><TableCell colSpan={10} align='center'>No bookings</TableCell></TableRow>)}
                {items.map((b) => {
                  const due = (b.totalAmount || 0) - (b.paidAmount || 0);
                  return (
                    <TableRow key={b.spaBookingId} hover>
                      <TableCell>{b.spaBookingId}</TableCell>
                      <TableCell>{b.bookingId || (b.isWalkIn ? 'Walk-in' : '-')}</TableCell>
                      <TableCell>{b.guestName}<br /><small>{b.guestMobile}</small></TableCell>
                      <TableCell>{b.roomNumber || (b.isWalkIn ? 'Walk-in' : '')}</TableCell>
                      <TableCell>{new Date(b.slotStart).toLocaleString('en-GB')}</TableCell>
                      <TableCell>
                        <Chip size='small' label={b.status}
                          color={b.status === 'Completed' ? 'success'
                                : b.status === 'Cancelled' ? 'error'
                                : b.status === 'InProgress' ? 'warning' : 'info'} />
                      </TableCell>
                      <TableCell align='right'>{currency} {Number(b.totalAmount).toFixed(2)}</TableCell>
                      <TableCell align='right'>{currency} {Number(b.paidAmount).toFixed(2)}</TableCell>
                      <TableCell align='right'>
                        <strong style={{ color: due > 0 ? '#c62828' : '#2e7d32' }}>
                          {currency} {Number(due).toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='View Items'>
                          <IconButton size='small' onClick={() => openDetails(b.spaBookingId)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Add Payment'>
                          <span>
                            <IconButton size='small' disabled={b.status === 'Cancelled'} onClick={() => openPayment(b)}>
                              <PaymentIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title='Invoice'>
                          <IconButton size='small' onClick={() => onInvoice(b.spaBookingId)}><ReceiptIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title='Cancel'>
                          <span>
                            <IconButton size='small' sx={{ color: '#2563eb' }} disabled={b.status === 'Cancelled'} onClick={() => onCancel(b.spaBookingId)}>
                              <CancelIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      {/* ---- New Booking Dialog ---- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>New Spa Booking</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size='small'
                label='In-house guest (auto-fills + charges to room)'
                value={selectedGuestId}
                onChange={(e) => onPickGuest(e.target.value)}
                InputProps={{ startAdornment: <HotelIcon fontSize='small' sx={{ mr: 1, color: 'action.active' }} /> }}>
                <MenuItem value=''>--Walk-in / manual--</MenuItem>
                {guests.map((g) => (
                  <MenuItem key={g.bookingId} value={g.bookingId}>
                    Room {g.roomNumber}  {g.guestName} (#{g.bookingId})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Guest Name' value={form.guestName}
                onChange={(e) => setForm({ ...form, guestName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Mobile' value={form.guestMobile}
                onChange={(e) => setForm({ ...form, guestMobile: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={form.isWalkIn}
                onChange={(e) => setForm({ ...form, isWalkIn: e.target.checked })} />} label='Walk-in' />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label='Room' value={form.roomNumber}
                disabled={form.isWalkIn}
                onChange={(e) => setForm({ ...form, roomNumber: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type='datetime-local' label='Slot start'
                InputLabelProps={{ shrink: true }} value={form.slotStart}
                onChange={(e) => setForm({ ...form, slotStart: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type='datetime-local' label='Slot end'
                InputLabelProps={{ shrink: true }} value={form.slotEnd}
                onChange={(e) => setForm({ ...form, slotEnd: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}>Services</Divider>

          <Box display='flex' justifyContent='flex-end' mb={1}>
            <Button size='small' startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          </Box>

          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table size='small' sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Therapist</TableCell>
                  <TableCell align='right'>Price</TableCell>
                  <TableCell align='right'>GST</TableCell>
                  <TableCell align='right'>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField select size='small' value={it.serviceId}
                        onChange={(e) => onPickService(idx, parseInt(e.target.value, 10))}
                        sx={{ minWidth: 180 }}>
                        <MenuItem value={0}>--Select--</MenuItem>
                        {services.map((s) => (
                          <MenuItem key={s.serviceId} value={s.serviceId}>{s.serviceName}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField select size='small' value={it.therapistId || ''}
                        onChange={(e) => updateLine(idx, { therapistId: e.target.value ? parseInt(e.target.value, 10) : null })}
                        sx={{ minWidth: 140 }}>
                        <MenuItem value=''>--None--</MenuItem>
                        {therapists.map((t) => (
                          <MenuItem key={t.therapistId} value={t.therapistId}>{t.therapistName}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    {(() => {
                      const lineGst = it.gstAmount || 0;
                      return (
                        <>
                          <TableCell align='right'>{Number(it.unitPrice).toFixed(2)}</TableCell>
                          <TableCell align='right'>{Number(lineGst).toFixed(2)}</TableCell>
                          <TableCell align='right'>{Number((it.unitPrice || 0) + lineGst).toFixed(2)}</TableCell>
                        </>
                      );
                    })()}
                    <TableCell>
                      <IconButton size='small' color='error' onClick={() => removeLine(idx)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type='number' label='Discount'
                value={form.discountAmount}
                onChange={(e) => setForm({ ...form, discountAmount: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={form.chargeToRoom}
                onChange={(e) => setForm({ ...form, chargeToRoom: e.target.checked })} />} label='Charge to room' />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label='Remarks' value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </Grid>
          </Grid>

          <Box display='flex' flexDirection='column' alignItems='flex-end' mt={2} gap={0.5}>
            <Typography variant='body2'>Subtotal: <strong>{currency} {totals.sub.toFixed(2)}</strong></Typography>
            <Typography variant='body2'>
              GST: <strong>{currency} {totals.gst.toFixed(2)}</strong>
            </Typography>
            {(parseFloat(form.discountAmount) || 0) > 0 && (
              <>
                <Typography variant='body2'>
                  Gross: <strong>{currency} {(totals.sub + totals.gst).toFixed(2)}</strong>
                </Typography>
                <Typography variant='body2' color='error'>
                  Discount:  {currency} {(parseFloat(form.discountAmount) || 0).toFixed(2)}
                </Typography>
              </>
            )}
            <Typography variant='h6' sx={{ mt: 0.5 }}>
              Total: <strong>{currency} {totals.tot.toFixed(2)}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onCreate}>Create Booking</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Payment Dialog ---- */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth='xs' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label='Mode' value={payment.paymentMode}
                onChange={(e) => setPayment({ ...payment, paymentMode: e.target.value })}>
                {['Cash', 'Card', 'UPI', 'Wallet', 'Room'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type='number' label='Amount' value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label='Reference No' value={payment.referenceNo}
                onChange={(e) => setPayment({ ...payment, referenceNo: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onAddPayment}>Save Payment</Button>
        </DialogActions>
      </Dialog>

      {/* ---- View Details Dialog ---- */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth='md' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>
          Spa Booking Details
          {detailsBooking?.bookingId ? ` — Linked Booking #${detailsBooking.bookingId}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading && <CircularProgress size={24} />}
          {!detailsLoading && detailsBooking && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}><strong>Guest:</strong> {detailsBooking.guestName}</Grid>
                <Grid item xs={6} sm={3}><strong>Mobile:</strong> {detailsBooking.guestMobile || '-'}</Grid>
                <Grid item xs={6} sm={3}><strong>Room:</strong> {detailsBooking.roomNumber || (detailsBooking.isWalkIn ? 'Walk-in' : '-')}</Grid>
                <Grid item xs={6} sm={3}><strong>Status:</strong> {detailsBooking.status}</Grid>
                <Grid item xs={6} sm={3}><strong>Slot:</strong> {new Date(detailsBooking.slotStart).toLocaleString('en-GB')}</Grid>
                <Grid item xs={6} sm={3}><strong>Booking #:</strong> {detailsBooking.bookingId || 'Walk-in'}</Grid>
                <Grid item xs={6} sm={3}><strong>Charge to Room:</strong> {detailsBooking.chargeToRoom ? 'Yes' : 'No'}</Grid>
                <Grid item xs={6} sm={3}><strong>Paid:</strong> {currency} {Number(detailsBooking.paidAmount).toFixed(2)}</Grid>
              </Grid>
              <Divider sx={{ my: 2 }}>Services</Divider>
              <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table size='small' sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Therapist</TableCell>
                      <TableCell align='right'>Duration</TableCell>
                      <TableCell align='right'>Unit Price</TableCell>
                      <TableCell align='right'>GST</TableCell>
                      <TableCell align='right'>Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailsBooking.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.serviceName || `#${it.serviceId}`}</TableCell>
                        <TableCell>{it.therapistName || '-'}</TableCell>
                        <TableCell align='right'>{it.durationMinutes} min</TableCell>
                        <TableCell align='right'>{currency} {Number(it.unitPrice).toFixed(2)}</TableCell>
                        <TableCell align='right'>{currency} {Number(it.gstAmount).toFixed(2)}</TableCell>
                        <TableCell align='right'>{currency} {Number(it.lineTotal).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display='flex' justifyContent='flex-end' mt={2} gap={3}>
                <Typography>Subtotal: <strong>{currency} {Number(detailsBooking.subTotal).toFixed(2)}</strong></Typography>
                <Typography>GST: <strong>{currency} {Number(detailsBooking.gstAmount).toFixed(2)}</strong></Typography>
                {Number(detailsBooking.discountAmount) > 0 && (
                  <Typography color='error'>Discount:  {currency} {Number(detailsBooking.discountAmount).toFixed(2)}</Typography>
                )}
                <Typography variant='h6'>Total: <strong>{currency} {Number(detailsBooking.totalAmount).toFixed(2)}</strong></Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailsBooking && (
            <Button
              variant='contained'
              startIcon={<ReceiptIcon />}
              onClick={() => printSpaInvoice(detailsBooking)}>
              Print Invoice
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
